import {createContext, FC, useContext, useEffect, useState} from "react";
import {AuthContext, AuthContextI} from "./auth/FirebaseAuth";
import {useLazyQuery} from "@apollo/client";
import {getUserQuery} from "./components/spots/graphQl";
import {User} from "./auth/types";
import NoAccessGate from "./auth/NoAccessGate";

type Props = {
    children: any
}

type UserStatus = "idle" | "authorized" | "no-access" | "error";

export interface UserContextI {
    getUser: () => User | undefined,
}

export const UserContext = createContext<UserContextI>({
    getUser: () => undefined,
});

const buildTraceId = () => `auth-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

export const UserContextProvider: FC<Props> = ({children}) => {
    const authContext = useContext<AuthContextI>(AuthContext)
    const [getUser] = useLazyQuery(getUserQuery);

    const [user, setUser] = useState<User | undefined>(undefined);
    const [status, setStatus] = useState<UserStatus>("idle");
    const [isLoading, setLoading] = useState<boolean>(false);
    const [refreshNonce, setRefreshNonce] = useState<number>(0);

    const doGetUser = async (email: string, authToken: string) => {
        const traceId = buildTraceId();
        console.log("[auth-trace] Requesting getUser", {
            traceId,
            email,
            tokenLength: authToken.length
        });
        setLoading(true);
        try {
            const response = await getUser({
                variables: {email: email, token: authToken},
                context: {
                    headers: {
                        "x-auth-trace-id": traceId
                    }
                }
            });

            console.log("[auth-trace] getUser response received", {
                traceId,
                hasData: !!response.data,
                hasError: !!response.error,
                authenticated: response.data?.getUser?.authenticated ?? null,
                graphQLErrorMessages: response.error?.graphQLErrors?.map((item) => item.message) || [],
                networkError: response.error?.networkError ? String(response.error.networkError) : undefined
            });

            if (response.data?.getUser?.authenticated) {
                setUser(response.data.getUser);
                setStatus("authorized");
                return;
            }

            console.warn("[auth-trace] getUser returned unauthenticated or empty data — user has no organization", {
                traceId
            });
            setUser(undefined);
            setStatus("no-access");
        } catch (error) {
            console.error("[auth-trace] getUser request failed", {
                traceId,
                email
            }, error);
            setStatus("error");
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        let isMounted = true;
        const loadUser = async () => {
            const authUser = authContext.getAuthUser();
            console.log("[auth-trace] loadUser invoked", {
                email: authUser?.email || null,
                hasAuthUser: !!authUser
            });
            if (!authUser?.email) {
                if (isMounted) {
                    setUser(undefined);
                    setStatus("idle");
                }
                return;
            }
            const token = await authContext.getTokenAsync();
            if (!token || !isMounted) {
                console.warn("[auth-trace] Missing token while loading user", {
                    email: authUser.email,
                    isMounted
                });
                return;
            }
            await doGetUser(authUser.email, token);
        };
        loadUser();
        return () => {
            isMounted = false;
        };
    }, [authContext, refreshNonce]);

    if (status === "no-access") {
        return <NoAccessGate
            onRefresh={() => setRefreshNonce((nonce) => nonce + 1)}
            isRefreshing={isLoading}
        />;
    }

    return <UserContext.Provider value={{
        getUser: () => user
    }}>{children}</UserContext.Provider>
}
